// websocket.js - Vuex Store Modul für WebSocket-Funktionalitäten
export default {
  namespaced: true,

  state: () => ({
    socket: null,
    isConnected: false,
    reconnectInterval: null,
    reconnectAttempts: 0,
    maxReconnectAttempts: 999,
    reconnectTimeoutMs: 3000,
    lastMessage: null,
    messages: [],
    error: null,
    clientId: null
  }),

  mutations: {
    SET_SOCKET(state, socket) {
      state.socket = socket
    },

    SET_CONNECTED(state, value) {
      state.isConnected = value
    },

    SET_RECONNECT_INTERVAL(state, interval) {
      state.reconnectInterval = interval
    },

    SET_RECONNECT_ATTEMPTS(state, attempts) {
      state.reconnectAttempts = attempts
    },

    ADD_MESSAGE(state, message) {
      state.messages.push(message)
      state.lastMessage = message
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    CLEAR_MESSAGES(state) {
      state.messages = []
      state.lastMessage = null
    },

    SET_CLIENT_ID(state, id) {
      state.clientId = id
    }
  },

  actions: {
    /**
     * WebSocket-Verbindung initialisieren und herstellen
     * @param {Object} context - Vuex-Kontext
     * @param {Object} options - Verbindungsoptionen
     * @param {string} options.url - WebSocket-URL
     */
    connect({ commit, dispatch, state, rootState }, options = {}) {
      // Nicht im SSR ausführen
      if (typeof window === 'undefined') return

      // Aktuellen Host und Protokoll verwenden, falls keine URL angegeben
      if (!options.url) {
        options.url = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/socket`
      }

      const url = options.url
      console.log('Connecting to WebSocket:', url)

      try {
        // Close existing WebSocket connection
        if (state.socket && state.socket.readyState !== WebSocket.CLOSED) {
          state.socket.close()
        }

        // New WebSocket connection
        const socket = new WebSocket(url)
        commit('SET_SOCKET', socket)

        // Event handler
        socket.onopen = () => {
          console.log('WebSocket connected:', url)
          commit('SET_CONNECTED', true)
          commit('SET_RECONNECT_ATTEMPTS', 0)

          if (state.reconnectInterval) {
            clearInterval(state.reconnectInterval)
            commit('SET_RECONNECT_INTERVAL', null)
          }

          // Send auth token if available
          // if (rootState.auth && rootState.auth.token) {
          //   dispatch('sendMessage', {
          //     type: 'auth',
          //     token: rootState.auth.token
          //   })
          // }

          // Process connected event
          dispatch('onConnected')
        }

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            commit('ADD_MESSAGE', data)

            // handle special message types
            dispatch('handleMessage', data)
          } catch (e) {
            console.error('Error parsing websocket event data:', e)
            commit('ADD_MESSAGE', { type: 'error', raw: event.data })
          }
        }

        socket.onclose = (event) => {
          commit('SET_CONNECTED', false)
          console.log(`Websocket closed, Code: ${event.code}, Reason: ${event.reason}`)

          // Connection loss event handling
          dispatch('onDisconnected', event)

          // Reconnect
          if (!event.wasClean && state.reconnectAttempts < state.maxReconnectAttempts) {
            console.log(`Reconnecting in ${state.reconnectTimeoutMs}ms...`)

            if (state.reconnectInterval) {
              clearInterval(state.reconnectInterval)
            }

            const interval = setInterval(() => {
              if (state.reconnectAttempts < state.maxReconnectAttempts) {
                commit('SET_RECONNECT_ATTEMPTS', state.reconnectAttempts + 1)
                console.log(`Reconnection tries ${state.reconnectAttempts}/${state.maxReconnectAttempts}`)
                dispatch('connect', options)
              } else {
                clearInterval(interval)
                commit('SET_RECONNECT_INTERVAL', null)
                console.error('Max reconnect attempts reached')
                commit('SET_ERROR', 'Connection lost, max reconnect attempts reached')
              }
            }, state.reconnectTimeoutMs)

            commit('SET_RECONNECT_INTERVAL', interval)
          }
        }

        socket.onerror = (error) => {
          console.error('Websocket error:', error)
          commit('SET_ERROR', 'Websocket error')

          // Error event handling
          dispatch('onError', error)
        }
      } catch (error) {
        console.error('Error while establishing websocket', error)
        commit('SET_ERROR', `Connection error: ${error.message}`)
      }
    },

    /**
     * Disconnect WebSocket
     */
    disconnect({ commit, state }) {
      if (state.reconnectInterval) {
        clearInterval(state.reconnectInterval)
        commit('SET_RECONNECT_INTERVAL', null)
      }

      if (state.socket && state.socket.readyState !== WebSocket.CLOSED) {
        state.socket.close(1000, 'Manually closed')
      }

      commit('SET_CONNECTED', false)
    },

    /**
     * Sending message
     * @param {Object} context - Vuex context
     * @param {Object|string} message - message to send
     * @returns {boolean} - success or failure
     */
    sendMessage({ state }, message) {
      if (!state.socket || state.socket.readyState !== WebSocket.OPEN) {
        console.warn('Websocket not connected. Message not sent:', message)
        return false
      }

      try {
        const messageStr = typeof message === 'string' ? message : JSON.stringify(message)
        state.socket.send(messageStr)
        return true
      } catch (error) {
        console.error('Error while sending websocket message:', error)
        return false
      }
    },

    onSystemMessage({ dispatch }, message) {
      console.log('Websocket: ', message.message)
      if (message.message === 'Connection established') {
        // need a random delay to avoid overloading the server on restart
        setTimeout(() => {
          dispatch('sendMessage', {
            type: 'request',
            command: 'initialNotifications'
          })
        }, Math.floor(Math.random() * 20000))
      }
    },
    onPongMessage() {
      // console.log('Websocket pong')
    },
    onReadCommentsConfirmedMessage({ dispatch }, { commentIds }) {
      dispatch('markAsSentComments', commentIds, { root: true })
    },

    /**
     * Processing incoming messages
     * @param {Object} context - Vuex context
     * @param {Object} message - received message
     */
    handleMessage({ commit, dispatch }, message) {
      // console.log('incoming message', message)
      // specific type handling
      switch (message.type) {
        case 'system':
          // system messages
          if (message.clientId) {
            commit('SET_CLIENT_ID', message.clientId)
          }
          break

        case 'error':
          // error messages
          commit('SET_ERROR', message.message || 'Serverfehler')
          break

        case 'initialNotifications':
          // initial notifications
          dispatch('info/initNotifications', message, { root: true })
          return

        case 'addSystemNotification':
          // add system notification as regular message
          dispatch('info/addMessage', message, { root: true })
          return

        case 'addCommentNotification':
          // add comment notification as regular message
          dispatch('info/addMessage', message, { root: true })
          return

        case 'removeCommentNotification':
          // remove comment notification
          dispatch('info/removeCommentNotifications', [message.commentId], { root: true })
          return

        case 'notification':
          // notification messages
          dispatch('handleNotification', message)
          break

        case 'update':
          // data update messages
          dispatch('handleDataUpdate', message)
          break
      }

      // Execute event for every message type
      if (message.type) {
        // console.log('message', `on${message.type.charAt(0).toUpperCase() + message.type.slice(1)}Message`)
        dispatch(`on${message.type.charAt(0).toUpperCase() + message.type.slice(1)}Message`, message)
      }
    },

    /**
     * On successful connection
     * @param {Object} context - Vuex context
     */
    onConnected({ dispatch }) {
      // Start ping interval
      dispatch('startPingInterval')

      // Additional actions on successful connection can be performed here
      // e.g., fetching initial data, updating status, etc.
    },

    /**
     * On socket disconnect
     * @param {Object} context - Vuex Context
     * @param {Event} event - close event
     */
    onDisconnected({ dispatch }, event) {
      // Additional actions on disconnect can be performed here
      dispatch('info/clearMessages', null, { root: true })
    },

    /**
     * On socket error
     * @param {Object} context - Vuex context
     * @param {Event} error - error event
     */
    onError({ commit }, error) {
      // Additional actions on error can be performed here
    },

    /**
     * Notification handling
     * @param {Object} context - Vuex context
     * @param {Object} message - notification data
     */
    handleNotification({ commit, dispatch }, message) {
      // Notification handling logic here
    },

    /**
     * Data update handling
     * @param {Object} context - Vuex context
     * @param {Object} message - update data
     */
    handleDataUpdate({ commit, dispatch, rootState }, message) {
      // Logic for data updates here
      if (message.entity && message.data) {
        // Example: Update profile color styles
        switch (message.entity) {
          case 'profileColorStyles':
            if (message.artistId === rootState.artist._id && rootState.profileColorStyles) {
              dispatch('setProfileColorStyles', message.data, { root: true })
            }
            break
        }
      }
    },

    /**
     * Start ping interval to keep connection alive
     * @param {Object} context - Vuex context
     */
    startPingInterval({ state, dispatch }) {
      if (typeof window === 'undefined') return

      // every 30 seconds
      const pingInterval = setInterval(() => {
        if (state.isConnected) {
          dispatch('sendMessage', { type: 'ping', timestamp: Date.now() })
        } else {
          clearInterval(pingInterval)
        }
      }, 30000)
    },

    /**
     * Init websocket
     * @param {Object} context - Vuex context
     * @param {Object} options - intialization options
     */
    init({ dispatch }, options = {}) {
      if (typeof window !== 'undefined') {
        // Ensure everything else is initialized properly
        setTimeout(() => {
          console.log('dispatching connect')
          dispatch('connect', options)
        }, 100)
      }
    }
  },

  getters: {
    isConnected: state => state.isConnected,
    lastMessage: state => state.lastMessage,
    messages: state => state.messages,
    clientId: state => state.clientId,
    error: state => state.error,
    isReconnecting: state => state.reconnectInterval !== null
  }
}
